import { useApiAvailability } from "@babackapp/private-api-client/client/use-api-availability";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { Button } from "@babackapp/customer-ui/components/button";
import { AlertTriangleIcon } from "lucide-react";
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  resolveOrderIdentificationError,
  type OrderIdentificationError,
} from "../utils/order-identification-error";
import { WidgetUnavailable } from "./widget-unavailable";

type ErrorBoundaryState = { error: Error | null };

// Browsers word a failed fetch differently: Chrome "Failed to fetch", Firefox
// "NetworkError when attempting to fetch resource", Safari "Load failed".
const NETWORK_ERROR_MESSAGES = /failed to fetch|networkerror|load failed/i;

function isNetworkError(error: Error): boolean {
  if (error instanceof AggregateError) {
    return error.errors.some((e) => e instanceof Error && isNetworkError(e));
  }
  return NETWORK_ERROR_MESSAGES.test(error.message);
}

export class WidgetErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ReturnWidget] Error boundary caught:", error, info.componentStack);
    // AggregateError (thrown by Jotai's flushCallbacks) hides the real cause(s) in `.errors`.
    // Unwrap them so we see the actual throw, not just "AggregateError".
    if (error instanceof AggregateError) {
      error.errors.forEach((e, i) => {
        console.error(
          `[ReturnWidget] AggregateError cause #${i}:`,
          e,
          (e as Error)?.stack,
        );
      });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <WidgetErrorFallback
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function WidgetErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const { t, i18n } = useTranslation("return-widget-v3");
  const availability = useApiAvailability();
  // The shop was already fetched (suspended on) by the parent layout before this
  // boundary's children could render, so this reads the cache instead of refetching.
  const { data: shop } = usePublicShop(shopId!);

  // A suspense query that failed because the request never completed lands here, and
  // the Back button below would navigate to the root, re-suspend and throw again — an
  // endless loop. Show the unavailable screen (reload, not navigate) instead.
  if (availability === "unreachable" || isNetworkError(error)) {
    return <WidgetUnavailable reason="api-down" />;
  }

  const handleReset = () => {
    onReset();
    // Navigate to the widget root (/return-widget/:shopId) to avoid leaving the widget
    // context entirely when there's no prior history entry within the app. Read shopId
    // from the router (not window.location.pathname) so it works under HashRouter too.
    navigate(`/return-widget/${shopId}`);
  };

  // A direct order link skips the identification screen, so a refused order
  // (expired, not fulfilled, unsupported country, ...) throws here. Resolve it the
  // way that screen does: the shop's configured text when there is one, the
  // translated title and description otherwise.
  const identificationError = resolveOrderIdentificationError({
    error,
    t,
    userPortal: shop?.preferences?.userPortal,
    language: i18n.language ?? "en",
  });

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <AlertTriangleIcon className="size-10 text-destructive" />
      <WidgetErrorMessage
        identificationError={identificationError}
        fallbackTitle={t("common.error.title")}
      />
      <Button variant="link" onClick={handleReset}>
        {t("common.back")}
      </Button>
    </div>
  );
}

type WidgetErrorMessageProps = {
  identificationError: OrderIdentificationError;
  fallbackTitle: string;
};

function WidgetErrorMessage({ identificationError, fallbackTitle }: WidgetErrorMessageProps) {
  const { code, title, description, html } = identificationError;

  if (html) {
    return <div className="font-sans text-sm" dangerouslySetInnerHTML={{ __html: html }} />;
  }

  // An unrecognised throw is a crash of the widget itself: the "unknown" copy of
  // the identification screen talks about an order that could not be found, which
  // would mislead the shopper here.
  if (code === "unknown") {
    return <p className="font-sans text-sm font-medium">{fallbackTitle}</p>;
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="font-sans text-sm font-medium">{title}</p>
      <p className="font-sans text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
